import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Banknote, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/lib/seo";
import { useCart } from "@/lib/cart";
import { useSettings } from "@/lib/settings";
import { useContent } from "@/lib/content";
import { useCustomer } from "@/lib/customer";
import { formatPrice, DAKAR_QUARTIERS } from "@/lib/format";

export default function Commande() {
  const { items, subtotal, setQty, remove, clear } = useCart();
  const settings = useSettings();
  const { t } = useContent();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { customer } = useCustomer();
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", customer_zone: "", customer_address: "", note: "" });
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!customer || prefilled) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || customer.name || "",
      customer_phone: f.customer_phone || customer.phone || "",
      customer_email: f.customer_email || customer.email || "",
      customer_zone: f.customer_zone || customer.zone || "",
      customer_address: f.customer_address || customer.address || "",
    }));
    setPrefilled(true);
  }, [customer, prefilled]);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [checking, setChecking] = useState(false);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setChecking(true);
    setPromoError("");
    try {
      const { data } = await api.post("/promo/check", { code: promoInput.trim(), subtotal });
      setPromo(data);
    } catch (err) {
      setPromo(null);
      setPromoError(formatApiError(err));
    } finally {
      setChecking(false);
    }
  };

  const discount = promo ? Math.min(promo.kind === "fixed" ? promo.value : Math.round((subtotal * promo.value) / 100), subtotal) : 0;
  const total = subtotal - discount + settings.delivery_fee;

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Votre panier est vide");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/orders", {
        ...form,
        items: items.map(({ id, qty, variant }) => ({ product_id: id, qty, variant: variant || "" })),
        promo_code: promo?.code || "",
      });
      clear();
      navigate(`/merci?ref=${res.data.ref}`, { state: { name: form.customer_name, email: form.customer_email, phone: form.customer_phone } });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="min-h-[70vh] pt-36 pb-20 flex flex-col items-center justify-center gap-5 px-6 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cream">
          <ShoppingBag size={26} className="text-teal" />
        </span>
        <p className="font-serif text-3xl text-teal-dark">{t("checkout.empty_title")}</p>
        <p className="text-ink/70 max-w-sm">{t("checkout.empty_text")}</p>
        <Link data-testid="checkout-empty-cta" to="/boutique" className="rounded-full bg-teal px-8 py-3 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
          Découvrir la boutique
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <SEO title="Finaliser ma commande" description="Finalisez votre commande : paiement à la livraison, expédition sous 24h partout à Dakar." />
      <section className="pt-32 md:pt-40 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="eyebrow mb-3">{t("checkout.eyebrow")}</p>
          <h1 className="font-serif text-3xl md:text-5xl text-teal-dark">{t("checkout.title")}</h1>

          <form data-testid="checkout-form" onSubmit={submit} className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 bg-white rounded-3xl border border-line p-6 md:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="font-serif text-2xl text-teal-dark">{t("checkout.form_title")}</h2>
                {customer ? (
                  <span data-testid="checkout-logged-in" className="rounded-full bg-mint/40 px-3 py-1 text-xs text-teal-dark">Connectée — {customer.email}</span>
                ) : (
                  <Link to="/compte/connexion" data-testid="checkout-login-link" className="text-xs text-teal link-underline">Déjà cliente ? Me connecter</Link>
                )}
              </div>
              <div className="space-y-5">
                <div>
                  <label htmlFor="co-name" className="text-sm font-medium text-ink">Nom complet *</label>
                  <input
                    id="co-name"
                    data-testid="checkout-input-name"
                    required
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    placeholder="Ex : Aïssatou Diop"
                    className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
                  />
                </div>
                <div>
                  <label htmlFor="co-phone" className="text-sm font-medium text-ink">Téléphone (WhatsApp ou appel) *</label>
                  <input
                    id="co-phone"
                    data-testid="checkout-input-phone"
                    required
                    type="tel"
                    value={form.customer_phone}
                    onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                    placeholder="Ex : +221 77 123 45 67"
                    className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
                  />
                </div>
                <div>
                  <label htmlFor="co-email" className="text-sm font-medium text-ink">Email (optionnel — pour retrouver vos commandes)</label>
                  <input
                    id="co-email"
                    data-testid="checkout-input-email"
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                    placeholder="Ex : aissatou@gmail.com"
                    className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
                  />
                </div>
                <div>
                  <label htmlFor="co-zone" className="text-sm font-medium text-ink">Quartier à Dakar *</label>
                  <select
                    id="co-zone"
                    data-testid="checkout-input-zone"
                    required
                    value={form.customer_zone}
                    onChange={(e) => setForm({ ...form, customer_zone: e.target.value })}
                    className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
                  >
                    <option value="">Choisissez votre quartier…</option>
                    {DAKAR_QUARTIERS.map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="co-address" className="text-sm font-medium text-ink">Adresse précise (rue, repère…) *</label>
                  <input
                    id="co-address"
                    data-testid="checkout-input-address"
                    required
                    value={form.customer_address}
                    onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                    placeholder="Ex : Cité Keur Gorgui, immeuble rose, 3e étage"
                    className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
                  />
                </div>
                <div>
                  <label htmlFor="co-note" className="text-sm font-medium text-ink">Note pour la livreuse (optionnel)</label>
                  <textarea
                    id="co-note"
                    data-testid="checkout-input-note"
                    rows={3}
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Ex : appeler avant d'arriver, livrer après 17h…"
                    className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="sticky top-28 bg-cream rounded-3xl border border-line p-6 md:p-8">
                <h2 className="font-serif text-2xl text-teal-dark mb-5">{t("checkout.summary_title")}</h2>
                <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
                  {items.map((item) => (
                    <div key={item.key} data-testid={`checkout-item-${item.key}`} className="flex gap-3 items-center">
                      <img src={item.image} alt="" className="h-14 w-12 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-teal-dark truncate">{item.name}{item.variant && <span className="ml-2 rounded-full bg-mint/30 px-2 py-0.5 text-[11px] font-normal">{item.variant}</span>}</p>
                        <div className="flex items-center gap-2 text-xs text-mauve mt-0.5">
                          <button type="button" data-testid={`checkout-qty-minus-${item.key}`} onClick={() => setQty(item.key, item.qty - 1)} className="underline">−</button>
                          <span>Qté {item.qty}</span>
                          <button type="button" data-testid={`checkout-qty-plus-${item.key}`} onClick={() => setQty(item.key, item.qty + 1)} disabled={item.qty >= item.stock} className="underline disabled:opacity-40">+</button>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-teal">{formatPrice(item.price * item.qty)}</p>
                      <button type="button" data-testid={`checkout-remove-${item.key}`} onClick={() => remove(item.key)} className="text-xs text-mauve underline hover:text-[#b04a4a]">Retirer</button>
                    </div>
                  ))}
                </div>
                <div className="mt-6 border-t border-tan/60 pt-4">
                  <label className="text-xs font-medium text-ink">{t("checkout.promo_label")}</label>
                  <div className="mt-1.5 flex gap-2">
                    <input
                      data-testid="checkout-promo-input"
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromo(null); setPromoError(""); }}
                      placeholder="MAMAN10"
                      className="flex-1 rounded-2xl border border-line bg-white px-4 py-2.5 text-sm uppercase outline-none focus:border-teal"
                    />
                    <button type="button" data-testid="checkout-promo-apply" onClick={applyPromo} disabled={checking || !promoInput.trim()} className="rounded-2xl bg-teal-dark px-4 py-2.5 text-sm font-medium text-cream disabled:opacity-50">
                      {checking ? "…" : t("checkout.promo_apply")}
                    </button>
                  </div>
                  {promoError && <p data-testid="checkout-promo-error" className="mt-2 text-xs text-[#b04a4a]">{promoError}</p>}
                  {promo && <p data-testid="checkout-promo-ok" className="mt-2 text-xs text-teal-dark">Code {promo.code} appliqué — {promo.kind === "percent" ? `-${promo.value} %` : `-${formatPrice(promo.value)}`}</p>}
                </div>
                <div className="mt-4 space-y-2 border-t border-tan/60 pt-4 text-sm">
                  <div className="flex justify-between text-ink/80"><span>Sous-total</span><span>{formatPrice(subtotal)}</span></div>
                  {discount > 0 && <div className="flex justify-between text-[#b04a4a]"><span>Remise</span><span data-testid="checkout-discount">- {formatPrice(discount)}</span></div>}
                  <div className="flex justify-between text-ink/80"><span>{t("checkout.delivery_label")}</span><span data-testid="checkout-delivery-fee" className={settings.delivery_fee ? "" : "text-xs text-mauve text-right max-w-[60%]"}>{settings.delivery_fee ? formatPrice(settings.delivery_fee) : t("checkout.delivery_tbd")}</span></div>
                  <div className="flex justify-between text-lg font-semibold text-teal pt-2"><span>Total</span><span data-testid="checkout-total">{formatPrice(total)}</span></div>
                </div>
                <div className="mt-5 flex items-start gap-3 rounded-2xl bg-white p-4">
                  <Banknote size={20} className="text-teal shrink-0 mt-0.5" />
                  <p className="text-xs text-ink/75">{t("checkout.notice")}</p>
                </div>
                <button
                  type="submit"
                  data-testid="checkout-submit-button"
                  disabled={loading}
                  className="mt-6 w-full rounded-full bg-teal py-4 font-medium text-cream hover:bg-teal-dark transition-all active:scale-[0.99] flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading && <Loader2 size={17} className="animate-spin" />}
                  {loading ? "Envoi en cours…" : t("checkout.submit")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
